/**
 * hub-connections.js - shared ASP.NET Core SignalR connections (NET10-UPGRADE-PLAN.md, 4j).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * ASP.NET SignalR 2 gave every page ONE connection ($.connection.hub) multiplexing every hub,
 * and a generated ~/signalr/hubs script that hung a typed proxy off it per hub. Neither exists
 * in ASP.NET Core: each hub is its own endpoint and its own HubConnection.
 *
 * Several independent scripts can want the same hub on the same page - the navbar bell
 * (notifications.js) and the request list (AdvancedContactRequests/List.cshtml) both listen on
 * AdvancedContactRequestHub, the admin test page makes three. Letting each build its own
 * HubConnection would open three WebSockets to the same endpoint and, worse, calling start()
 * on a connection that is not Disconnected throws. So connections are created once, keyed by
 * hub name, and start() is idempotent: every caller gets the same in-flight promise.
 *
 * Loaded as the second half of the ~/Bundle/js/signalr.js bundle (see gulpfile.js), i.e. right
 * after the @microsoft/signalr UMD build that defines window.signalR, and before jQuery's
 * document-ready callbacks run.
 *
 * URLs are absolute from the site root and NOT tenant-scoped: the hubs are mapped with
 * app.MapHub<T>("/hubs/...") in Program.cs, outside the app/{company}/{shop}/... route table.
 * Tenant/shop context is passed as arguments to the hub methods instead, exactly as before.
 */
var GroomerHubs = (function (window) {
    'use strict';

    var signalR = window.signalR;

    /** Must match the app.MapHub<T>(...) paths in Program.cs. */
    var HUB_URLS = {
        calendar: '/hubs/calendar',
        advancedContactRequest: '/hubs/advancedContactRequest',
        payment: '/hubs/payment',
        googleCalendarSync: '/hubs/googleCalendarSync'
    };

    // name -> { connection: HubConnection, starting: Promise|null }
    var entries = {};

    function warn(message) {
        if (window.console && window.console.warn) {
            window.console.warn('GroomerHubs: ' + message);
        }
    }

    /** False when the signalr bundle failed to load - every caller checks this and degrades. */
    function available() {
        return !!(signalR && signalR.HubConnectionBuilder);
    }

    /**
     * The (lazily created, never started) connection for a hub, or null if the client library
     * is missing or the name is unknown. Register .on() handlers BEFORE calling start().
     */
    function get(name) {
        if (!available()) {
            return null;
        }

        var url = HUB_URLS[name];
        if (!url) {
            warn('unknown hub "' + name + '".');
            return null;
        }

        var entry = entries[name];
        if (!entry) {
            var connection = new signalR.HubConnectionBuilder()
                .withUrl(url)
                // Replaces the hand-rolled "wait 5s then start() again" loop the MVC5 code had
                // in Appointment/create.js; the Core client owns retry/backoff itself.
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            entry = entries[name] = { connection: connection, starting: null };

            // Fires only once automatic reconnect has given up (not between retries). Clearing
            // the cached promise is what lets a later start() - e.g. from the visibilitychange
            // handlers - actually reconnect instead of resolving against a dead connection.
            connection.onclose(function () {
                entry.starting = null;
            });
        }

        return entry.connection;
    }

    /**
     * Starts the hub connection if it is not already starting/started, and returns a promise
     * that resolves once it is connected. Safe to call repeatedly and from several scripts.
     */
    function start(name) {
        var connection = get(name);
        if (!connection) {
            return Promise.reject(new Error('SignalR client unavailable or unknown hub "' + name + '".'));
        }

        var entry = entries[name];

        // Connecting / Connected / Reconnecting: a start is already in flight or has succeeded.
        if (connection.state !== signalR.HubConnectionState.Disconnected) {
            return entry.starting || Promise.resolve();
        }

        if (!entry.starting) {
            entry.starting = connection.start().catch(function (err) {
                // Drop the cached promise so the next caller gets a real retry rather than the
                // same rejected promise forever.
                entry.starting = null;
                throw err;
            });
        }

        return entry.starting;
    }

    /** signalR.HubConnectionState value, or null when the connection was never created. */
    function state(name) {
        if (!available() || !entries[name]) {
            return null;
        }
        return entries[name].connection.state;
    }

    function isConnected(name) {
        return state(name) === signalR.HubConnectionState.Connected;
    }

    /** Populated by the server only AFTER start() resolves - null before that. */
    function connectionId(name) {
        if (!available() || !entries[name]) {
            return null;
        }
        return entries[name].connection.connectionId;
    }

    return {
        urls: HUB_URLS,
        available: available,
        get: get,
        start: start,
        state: state,
        isConnected: isConnected,
        connectionId: connectionId
    };
}(window));
